import React, { useState, useEffect, useMemo } from "react";
import { 
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Card, CardContent, CardHeader, Typography, Grid, Chip, Box, Divider 
} from '@material-ui/core';

export default function PublicTripPage({ tripId }) {
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!tripId) return;

    // Force hide existing navigation and footer elements
    const style = document.createElement('style');
    style.innerHTML = `
      .topnavigation, .footer, header, footer, .MuiBottomNavigation-root { display: none !important; }
      body { margin: 0 !important; padding: 0 !important; overflow-x: hidden; }
      #root > div:first-child { padding-top: 0 !important; }
    `;
    document.head.appendChild(style);

    const fetchTrip = async () => {
      try {
        const res = await fetch(`https://australia-southeast1-trip-planner-7fc5a.cloudfunctions.net/retrieveTrip?tripId=${tripId}`);
        if (!res.ok) throw new Error("Trip not found");
        const data = await res.json();
        setTrip(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchTrip();

    // Cleanup: Restore navigation when leaving the page
    return () => {
      document.head.removeChild(style);
    };
  }, [tripId]);

  const organizedData = useMemo(() => {
    if (!trip) return {};
    const groups = {};
    const isHiking = trip.Category === "HIKING" || trip.Category === "BIKEPACKING";

    const initContainer = (name) => {
      if (!groups[name]) groups[name] = { subCategories: {}, totalWeight: 0 };
    };

    const addToGroup = (container, subGroup, item, qty) => {
      if (!groups[container].subCategories[subGroup]) groups[container].subCategories[subGroup] = [];
      groups[container].subCategories[subGroup].push({ ...item, displayQty: qty });
      groups[container].totalWeight += (Number(item.Weight) * Number(qty));
    };

    trip.Gear?.forEach(item => {
      if (isHiking && item.gearHiking?.length > 0) {
        item.gearHiking.forEach(assign => {
          const traveler = assign.Name || "Shared";
          const loc = assign.packingLocation || "Base Weight";
          initContainer(traveler);
          addToGroup(traveler, loc, item, assign.Quantity);
        });
      } else {
        const location = item.packingLocation || "General";
        const cat = item.SubCategory || item.Category || "Gear";
        initContainer(location);
        addToGroup(location, cat, item, item.Quantity || 1);
      }
    });

    trip.Food?.forEach(item => {
      if (isHiking) {
        let name = "Shared";
        if (item.packingLocation?.startsWith("PACK - ")) {
          name = item.packingLocation.replace("PACK - ", "");
        } else if (item.Owner) {
          name = item.Owner;
        }
        initContainer(name);
        addToGroup(name, "Consumables", item, item.Quantity || 1);
      } else {
        const location = item.packingLocation || "General";
        initContainer(location);
        addToGroup(location, "Consumables", item, item.Quantity || 1);
      }
    });

    return groups;
  }, [trip]);

  if (loading) return <Box p={5} textAlign="center"><Typography variant="h6">Loading Adventure...</Typography></Box>;
  if (error) return <Box p={5} textAlign="center"><Typography color="error">{error}</Typography></Box>;

  return (
    <Box 
      style={{ 
        backgroundColor: '#f0f2f5', 
        minHeight: '100vh', 
        width: '100vw', 
        position: 'relative',
        zIndex: 1000 
      }}
    >
      <Box p={3} maxWidth="1200px" margin="0 auto">
        {/* Modern Standalone Header */}
        <Paper elevation={0} style={{ padding: '30px', marginBottom: '30px', borderRadius: '16px', background: '#111', color: '#fff', textAlign: 'center' }}>
          <Typography variant="h3" style={{ fontWeight: 800 }}>{trip.Name}</Typography>
          <Box mt={1} display="flex" justifyContent="center" alignItems="center">
            <Chip label={trip.Category} style={{ backgroundColor: '#ef4923', color: '#fff', fontWeight: 'bold' }} />
          </Box>
        </Paper>

        <Grid container spacing={3}>
          {Object.keys(organizedData).sort().map((containerName) => {
            const { subCategories, totalWeight } = organizedData[containerName];

            return (
              <Grid item xs={12} key={containerName}>
                <Card elevation={2} style={{ borderRadius: '16px', overflow: 'hidden' }}>
                  <CardHeader 
                    title={<Typography variant="h5" style={{ fontWeight: 700 }}>{containerName.toUpperCase()}</Typography>}
                    action={
                      <Box textAlign="right" p={1}>
                        <Typography variant="h6" style={{ color: '#ef4923', fontWeight: 800 }}>{totalWeight.toFixed(2)}kg</Typography>
                        <Typography variant="caption" color="textSecondary">TOTAL SECTION WEIGHT</Typography>
                      </Box>
                    }
                    style={{ background: '#fff', borderBottom: '1px solid #f0f0f0' }}
                  />
                  <CardContent style={{ padding: '20px' }}>
                    <Grid container spacing={4}>
                      {Object.keys(subCategories).sort().map((subName) => (
                        <Grid item xs={12} md={6} key={subName}>
                          <Typography variant="button" style={{ color: '#ef4923', fontWeight: 'bold', borderBottom: '2px solid #ef4923', marginBottom: '12px', display: 'inline-block' }}>
                            {subName}
                          </Typography>
                          <Table size="small">
                            <TableBody>
                              {subCategories[subName].map((item, i) => (
                                <TableRow key={i} hover>
                                  <TableCell style={{ border: 0, width: '15%', fontWeight: 'bold', fontSize: '1rem' }}>{item.displayQty}x</TableCell>
                                  <TableCell style={{ border: 0, width: '65%' }}>
                                    <Typography variant="body1" style={{ fontWeight: 500 }}>{item.Name}</Typography>
                                    <Typography variant="caption" color="textSecondary">
                                      {item.Brand !== "-" ? item.Brand : ''} {item.SubCategory ? `| ${item.SubCategory}` : ''}
                                    </Typography>
                                  </TableCell>
                                  <TableCell style={{ border: 0, width: '20%' }} align="right">
                                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                                      {(item.Weight * item.displayQty).toFixed(2)}kg
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Box mt={6} pb={4} textAlign="center" style={{ opacity: 0.5 }}>
          <Typography variant="caption">POWERED BY PACKU</Typography>
        </Box>
      </Box>
    </Box>
  );
}